import { useAuthStore } from "@features/auth/store/authStore";
import { getProfileImage, useProfile } from "@shared/hooks/useProfile";
import AuthenticatedLayout from "@shared/layouts/AuthenticatedLayout";
import { User } from "@shared/types/user";
import React, { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { useParams } from "react-router-dom";
import CreateGroupModal from "../components/CreateGroupModal";
import GroupTypingIndicator from "../components/GroupTypingIndicator";
import MessageInput from "../components/MessageInput";
import MessageList from "../components/MessageList";
import MessagesHeader from "../components/MessagesHeader";
import MessagesSidebar from "../components/MessagesSidebar";
import {
  ConversationsProvider,
  useConversations,
} from "../context/ConversationsContext";
import { useGroupConversations } from "../hooks/useGroupConversations";
import { useMessages } from "../hooks/useMessages";
import { GroupService } from "../services/group.service";
import {
  buildUserMap,
  fetchGroupParticipantsUserMap,
} from "../services/userMap.service";

const MessagesContent: React.FC = () => {
  const { conversationId: conversationIdFromUrl } = useParams<{ conversationId?: string }>();
  const [msgSidebarOpen, setMsgSidebarOpen] = useState(true);
  const [selectedConversation, setSelectedConversation] = useState<string | null>(
    conversationIdFromUrl ?? null
  );
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  // State for group modal
  const [showCreateGroupModal, setShowCreateGroupModal] = useState(false);
  const [groupTitle, setGroupTitle] = useState("");
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [color, setColor] = useState("#6366f1"); // default color (indigo)
  const [editGroupId, setEditGroupId] = useState<string | null>(null);

  const { user } = useProfile();
  const { conversations: rawConversations, userProfiles } = useConversations();
  const [userMap, setUserMap] = useState<Record<string, User>>({});
  const currentUser = useAuthStore((state) => state.user);
  const token = useAuthStore((state) => state.token);
  const userProfileIds = useMemo(
    () => Object.keys(userProfiles),
    [userProfiles]
  );

  useEffect(() => {
    const selectedConv = conversations.find(
      (c) => c.id === selectedConversation
    );
    if (selectedConv?.type === "group" && selectedConv.id && token) {
      fetchGroupParticipantsUserMap(selectedConv.id)
        .then((userMap) => {
          setUserMap(userMap);
        })
        .catch(console.error);
    } else if (userProfileIds.length > 0) {
      buildUserMap(userProfileIds, currentUser).then(setUserMap);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedConversation, userProfileIds, currentUser, token]);

  // Normalizes so that any conversation with 2 participants WITHOUT type is 'direct', and any conversation with more than 2 participants is 'group'.
  const conversations = rawConversations.map((conv) => ({
    ...conv,
    type: conv.type || (conv.participants.length === 2 ? "direct" : "group"),
  }));

  // Handler for group creation using Firestore
  const { groups, createGroupConversation } = useGroupConversations();

  // Handler for create/edit group
  const handleGroupModalSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!groupTitle.trim() || selectedUsers.length === 0) {
      toast.error("Please enter a group name and select at least one member.");
      return;
    }
    const participantIds = Array.from(new Set([...selectedUsers, user?.id!]));
    if (editGroupId) {
      // Edit group
      try {
        await GroupService.updateGroup(editGroupId, {
          title: groupTitle,
          participants: participantIds,
          color,
        });
        setShowCreateGroupModal(false);
        setEditGroupId(null);
        toast.success("Group updated!");
      } catch (err) {
        toast.error("Error updating group");
      }
    } else {
      // Create group
      const groupId = await createGroupConversation(
        groupTitle,
        participantIds,
        color
      );
      if (groupId) {
        setShowCreateGroupModal(false);
        setGroupTitle("");
        setSelectedUsers([]);
        setColor("#6366f1");
        setSelectedConversation(groupId);
        toast.success("Group created successfully!");
      } else {
        toast.error("Failed to create group. Please try again.");
      }
    }
  };

  // Helper to open modal in edit mode
  const openEditGroupModal = (group: any) => {
    if (group.createdBy !== user?.id) {
      toast.error("Only the creator of the group can edit it.");
      return;
    }
    setEditGroupId(group.id);
    setGroupTitle(group.title || "");
    setSelectedUsers(
      group.participants?.filter((id: string) => id !== user?.id) || []
    );
    setColor(group.color || "#6366f1");
    setShowCreateGroupModal(true);
  };

  const clearGroupModalState = () => {
    setEditGroupId(null);
    setGroupTitle("");
    setSelectedUsers([]);
    setColor("#6366f1");
  };

  const {
    messages,
    loading: messagesLoading,
    sendMessage,
    deleteMessage,
    editMessage,
    updateTypingStatus,
    hasMore,
    loadMoreMessages: loadMore,
    isTyping,
  } = useMessages(selectedConversation || "", selectedUser, userMap);

  const handleDeleteMessage = async (messageId: string) => {
    try {
      await deleteMessage(messageId);
      toast.success("Message deleted successfully");
    } catch (error) {
      console.error("Error deleting message:", error);
      toast.error("Failed to delete message. Please try again.");
      throw error;
    }
  };

  const handleEditMessage = async (messageId: string, newContent: string) => {
    try {
      await editMessage(messageId, newContent);
    } catch (error: any) {
      const errorMessage =
        error?.message === 'Edit time limit exceeded'
          ? 'You can only edit messages within 5 minutes of sending.'
          : 'Failed to edit message. Please try again.';
      toast.error(errorMessage);
      throw error;
    }
  };

  // Update selected user when conversation changes
  useEffect(() => {
    if (selectedConversation && conversations.length > 0) {
      const conversation = conversations.find(
        (conv) => conv.id === selectedConversation
      );
      if (conversation && conversation.type === "direct") {
        const otherUserId = conversation.participants.find(
          (id) => id !== user?.id
        );
        if (otherUserId) {
          setSelectedUser(userMap?.[otherUserId]);
        }
      } else {
        setSelectedUser(null);
      }
    } else {
      setSelectedUser(null);
    }
  }, [selectedConversation, conversations, user?.id]);

  return (
    <AuthenticatedLayout className="md:h-auto h-full overflow-hidden">
      <div className="relative flex h-full">
        {/* Messages sidebar */}
        <MessagesSidebar
          msgSidebarOpen={msgSidebarOpen}
          setMsgSidebarOpen={setMsgSidebarOpen}
          selectedConversation={selectedConversation}
          setSelectedConversation={setSelectedConversation}
          onOpenCreateGroupModal={() => {
            clearGroupModalState();
            setShowCreateGroupModal(true);
          }}
          groups={groups}
          onOpenEditGroupModal={openEditGroupModal}
        />
        {/* Create group modal rendered at root level */}
        <CreateGroupModal
          open={showCreateGroupModal}
          onClose={() => {
            setShowCreateGroupModal(false);
            clearGroupModalState();
          }}
          groupTitle={groupTitle}
          setGroupTitle={setGroupTitle}
          selectedUsers={selectedUsers}
          setSelectedUsers={setSelectedUsers}
          userProfiles={userProfiles}
          onSubmit={handleGroupModalSubmit}
          color={color}
          setColor={setColor}
          editMode={!!editGroupId}
          canEdit={(() => {
            if (!editGroupId) return true; // create
            const group = groups.find((g) => g.id === editGroupId);
            return group?.createdBy === user?.id;
          })()}
        />
        {/* Messages body */}
        <div
          className={`grow flex flex-col md:translate-x-0 transition-transform duration-300 ease-in-out overflow-hidden ${msgSidebarOpen ? "translate-x-1/3" : "translate-x-0"
            }`}
        >
          {selectedConversation ? (
            <>
              <MessagesHeader
                msgSidebarOpen={msgSidebarOpen}
                setMsgSidebarOpen={setMsgSidebarOpen}
                selectedUser={selectedUser}
                isTyping={(() => {
                  const conv = conversations.find(
                    (c) => c.id === selectedConversation
                  );
                  return conv && conv.type === "direct" ? isTyping : undefined;
                })()}
                selectedGroup={(() => {
                  const conv = conversations.find(
                    (c) => c.id === selectedConversation
                  );
                  return conv && conv.type === "group"
                    ? groups.find((g) => g.id === selectedConversation)
                    : undefined;
                })()}
                userProfiles={userMap}
                onEditGroup={openEditGroupModal}
              />
              <MessageList
                messages={messages}
                loading={messagesLoading}
                hasMore={hasMore}
                onLoadMore={loadMore}
                onDeleteMessage={handleDeleteMessage}
                onEditMessage={handleEditMessage}
                user={user}
                selectedUser={selectedUser}
                userProfiles={userMap}
                isGroup={(() => {
                  const conv = conversations.find(
                    (c) => c.id === selectedConversation
                  );
                  return conv && conv.type === "group";
                })()}
              />
              {/* Group typing indicator */}
              {(() => {
                const conv = conversations.find(
                  (c) => c.id === selectedConversation
                );
                let typingUsers: { id: string; name: string; photo: string }[] =
                  [];
                if (
                  conv &&
                  conv.type === "group" &&
                  isTyping &&
                  typeof isTyping === "object"
                ) {
                  typingUsers = Object.entries(isTyping)
                    .filter(([userId, typing]) => typing && userId !== user?.id)
                    .map(([userId]) => ({
                      id: userId,
                      name: userProfiles[userId]?.name,
                      photo: getProfileImage(userProfiles[userId]),
                    }))
                    .filter((u) => u.name);
                }
                return typingUsers.length > 0 ? (
                  <GroupTypingIndicator typingUsers={typingUsers} />
                ) : null;
              })()}
              <MessageInput
                onSendMessage={(content, type, mediaUrl) =>
                  sendMessage(content, type, mediaUrl)
                }
                updateTypingStatus={updateTypingStatus}
              />
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <p className="text-slate-500 dark:text-slate-400">
                Select a conversation to start messaging
              </p>
            </div>
          )}
        </div>
      </div>
    </AuthenticatedLayout>
  );
};

const MessagesPage: React.FC = () => {
  return (
    <ConversationsProvider>
      <MessagesContent />
    </ConversationsProvider>
  );
};

export default MessagesPage;
